import { CalendarIcon, ChevronDownIcon, CloseIcon } from "../icons";
import { DateRangeType, Ranges, formatDateRange } from "helper/date-time";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { useMemo, useState } from "react";

import { DateRangePickerSelector } from "./DateRangePicker";
import { IconButton } from "@/components";
import { Range } from "react-date-range";
import clsx from "clsx";
import { hasClassName } from "helper/check-class";
import { isEmpty } from "lodash";

type VariantButton = "primary" | "outline" | "secondary" | "error";

export interface DateRangeInputProps extends React.ComponentPropsWithoutRef<"button"> {
  from?: Date | null;
  to?: Date | null;
  onChangeValue: (newDateRange: Range, type?: DateRangeType) => void;
  placeholder?: string;
  selectedValue?: DateRangeType;
  variant?: VariantButton;
  minDate?: Date;
  maxDate?: Date;
}

export const DateRangeSelector = ({
  variant = "primary",
  selectedValue = "custom",
  from,
  to,
  onChangeValue,
  placeholder = "Période",
  className,
  minDate,
  maxDate,
  ...buttonProps
}: DateRangeInputProps) => {
  const [open, setOpen] = useState(false);
  const fromValue = from === null ? undefined : from;
  const toValue = to === null ? undefined : to;

  const displayValue = useMemo(() => {
    if (isEmpty(selectedValue) || selectedValue === "custom") {
      return formatDateRange(fromValue, toValue);
    }
    return Ranges.filter((item) => item.name === selectedValue)[0].display;
  }, [fromValue, toValue, selectedValue]);

  const handleChangeDate = (newDateRange: Range, type: DateRangeType) => {
    setOpen(false);
    onChangeValue(newDateRange, type);
  };

  return (
    <div className={clsx("flex items-center bg-white p-1 pl-3 rounded-md shadow-lg", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          className={clsx(
            "flex items-center min-w-0",
            buttonProps.disabled
              ? "bg-gray-200 cursor-default"
              : [btnClassByVariant[variant], !hasClassName("active:") && "active:shadow-outline"],
            !hasClassName(className, "w-") && "w-60",
          )}
        >
          <CalendarIcon color={colorByVariant[variant]} className="size-4" />
          <span className={clsx("flex-1 truncate text-sm text-center capitalize ml-1", textColorByVariant[variant])}>
            {displayValue || <span className="text-lightgrey">{placeholder}</span>}
          </span>
          <div className="flex w-6">
            <ChevronDownIcon color={colorByVariant[variant]} className="rotate-90" />
            <ChevronDownIcon className="-rotate-90" color={colorByVariant[variant]} />
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0 bg-transparent shadow-none w-full">
          <DateRangePickerSelector
            onSelectDate={handleChangeDate}
            selectedType={selectedValue}
            startDate={fromValue}
            endDate={toValue}
            minDate={minDate}
            maxDate={maxDate}
            onClose={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>
      <IconButton
        onClick={() => handleChangeDate({ startDate: undefined, endDate: undefined }, "custom")}
        className="flex w-6"
      >
        <CloseIcon className="text-black w-4 h-4" />
      </IconButton>
    </div>
  );
};

const btnClassByVariant: Record<VariantButton, string> = {
  primary: "bg-primary",
  secondary: "bg-transparent",
  outline: "bg-transparent",
  error: "bg-red-500",
};

const colorByVariant: Record<VariantButton, string> = {
  primary: "#F3F3F4",
  secondary: "#28292F",
  outline: "#28292F",
  error: "#28292F",
};

const textColorByVariant: Record<VariantButton, string> = {
  primary: "text-secondary",
  secondary: "text-primary",
  outline: "text-primary",
  error: "text-rose-500",
};
